import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  Typography,
  Card,
  CardContent,
  Divider,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  getMyCompany,
  useCreateMyCompany,
  useCreateMyCompanyProduct,
  useDeleteMyCompanyProduct,
  useUpdateMyCompany,
  useUpdateMyCompanyProduct,
} from '../queries/myCompany';
import Table from '../components/Table';
import CompanyDetailsForm from './components/CompanyDetailsForm';
import ProductDetailsForm from './components/ProductDetailsForm';
import DownloadButton from '../components/DownloadButton';
import ConfirmDialog from '../components/ConfirmDialog';
import CustomLink from '../components/CustomLink';
import { COLUMN_LABEL_MAP, DEFAULT_COMPANY_PRODUCT_TABLE_COLUMNS, PRODUCT_TABLE_COLUMNS_SETTING } from '../constants';
import { getSettingByTitle } from '../queries/settings';
import { formatValidNumber } from '../utils/helper';
import CompanyLogo from '../components/CompanyLogo';

const CompanyProfile = () => {
  const { data: companyData, isLoading, isRefetching, refetch } = useQuery(getMyCompany({}));
  const { data: columnsSetting } = useQuery(getSettingByTitle({ params: { title: PRODUCT_TABLE_COLUMNS_SETTING } }));

  const { mutate: createCompany } = useCreateMyCompany();
  const { mutate: updateCompany } = useUpdateMyCompany();
  const { mutate: createCompanyProduct } = useCreateMyCompanyProduct();
  const { mutate: updateCompanyProduct } = useUpdateMyCompanyProduct();
  const { mutate: deleteCompanyProduct } = useDeleteMyCompanyProduct();

  const [isCompanyModalOpen, setCompanyModalOpen] = useState(false);
  const [isProductModalOpen, setProductModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState();
  const [selectedProduct, setSelectedProduct] = useState();

  const columns = useMemo(() => {
    const raw = columnsSetting?.[0]?.content;
    if (!raw || typeof raw !== 'string') return DEFAULT_COMPANY_PRODUCT_TABLE_COLUMNS;
    const trimmed = raw.trim();
    if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) {
      return DEFAULT_COMPANY_PRODUCT_TABLE_COLUMNS;
    }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : DEFAULT_COMPANY_PRODUCT_TABLE_COLUMNS;
    } catch {
      return DEFAULT_COMPANY_PRODUCT_TABLE_COLUMNS;
    }
  }, [columnsSetting]);

  const visibleColumns = useMemo(() => {
    try {
      const mappedColumns = columns
        .filter((column) => !column.hidden)
        .map((column) => ({ ...column, label: COLUMN_LABEL_MAP[column.key] || column.label }));
      return [...mappedColumns, { key: 'actions', label: '', width: '100px' }];
    } catch (error) {
      return [];
    }
  }, [columns]);

  const tableData = useMemo(
    () =>
      companyData?.products?.map((product) => ({
        companyName: companyData.companyName,
        ...product,
        averageMRR: product.averageMRR || 'N/A',
        commissionFirstTerm: formatValidNumber(product.commissionFirstTerm),
        commissionRenewals: formatValidNumber(product.commissionRenewals),
        // averageTotalCommissionValue: formatValidNumber(product.averageTotalCommissionValue),
        averageMonthCommissionValue: formatValidNumber(product.averageMonthCommissionValue),
        averageConversionRate: formatValidNumber(product.averageConversionRate),
        churn: formatValidNumber(product.churn),
        averageSalesCycle: product.averageSalesCycle || 'N/A',
        duration: product.duration || 'No Limit',
        website:
          product.website || companyData.website ? (
            <Box
              color='inherit'
              component={CustomLink}
              to={product.website || companyData.website}
              target='_blank'
              sx={{
                display: 'block',
                textDecoration: 'none',
                color: 'primary.main',
                width: 'max-content',
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {product.website || companyData.website}
            </Box>
          ) : undefined,
        markets: product.markets?.join(', '),
        actions: (
          <Box display='flex' justifyContent='end'>
            <IconButton color='primary' onClick={() => handleSelectProduct(product)}>
              <EditIcon />
            </IconButton>
            <IconButton color='error' onClick={() => setSelectedProductId(product._id)}>
              <DeleteIcon />
            </IconButton>
          </Box>
        ),
        downloads: (
          <Box display='flex' flexDirection='column' gap={1}>
            {product.downloads?.map((download, index) => (
              <DownloadButton key={index} fileUrl={download.url} fileName={download.description} />
            ))}
          </Box>
        ),
      })) || [],
    [companyData],
  );

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setProductModalOpen(true);
  };

  const onCreateCompany = (data) => {
    createCompany(
      { data },
      {
        onSuccess: () => {
          refetch();
          toast('Created company successfully!', { type: 'success' });
        },
        onError: (error) => {
          toast('Failed to create company', { type: 'error' });
        },
      },
    );
  };

  const onUpdateCompany = (data) => {
    updateCompany(
      { data },
      {
        onSuccess: () => {
          refetch();
          setCompanyModalOpen(false);
          toast('Updated company details successfully!', { type: 'success' });
        },
        onError: (error) => {
          toast('Failed to update company', { type: 'error' });
        },
      },
    );
  };

  const onConfirmProduct = (data) => {
    if (!selectedProduct?.productName) {
      createCompanyProduct(
        { data },
        {
          onSuccess: () => {
            refetch();
            setProductModalOpen(false);
            toast('Created product successfully!', { type: 'success' });
          },
          onError: (error) => {
            toast('Failed to create product', { type: 'error' });
          },
        },
      );
    } else {
      updateCompanyProduct(
        { id: selectedProduct._id, data },
        {
          onSuccess: () => {
            refetch();
            setProductModalOpen(false);
            toast('Updated product successfully!', { type: 'success' });
          },
          onError: (error) => {
            toast('Failed to update product', { type: 'error' });
          },
        },
      );
    }
  };

  const handleDeleteProduct = () => {
    deleteCompanyProduct(
      { id: selectedProductId },
      {
        onSuccess: () => {
          refetch();
          setSelectedProductId(undefined);
          toast('Deleted product successfully!', { type: 'success' });
        },
        onError: (error) => {
          toast('Failed to delete product', { type: 'error' });
          setSelectedProductId(undefined);
        },
      },
    );
  };

  if (isLoading) return null;

  if (!companyData?._id) {
    return (
      <Box height='100%'>
        <CompanyDetailsForm isLoading={isLoading || isRefetching} onConfirm={onCreateCompany} />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ my: 4, px: { xs: 2, md: 3 }, width: '100%', maxWidth: '100%' }}>
      <Card variant='outlined' sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.06)', p: 3 }}>
        <CardContent>
          <Box display='flex' justifyContent='center' alignItems='center' mb={2} gap={2}>
            <CompanyLogo url={companyData.companyLogo} size='70px' isCircle={false} />
            <Typography variant='h4' fontWeight='bold'>
              {companyData.companyName}
            </Typography>
            <Button variant='outlined' size='small' onClick={() => setCompanyModalOpen(true)}>
              Edit Company
            </Button>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
            <Typography variant='h6' fontWeight='medium'>
              Programs
            </Typography>
            <Button variant='contained' size='small' onClick={() => handleSelectProduct({ company: companyData._id })}>
              Add Program
            </Button>
          </Box>

          <Table isLoading={isLoading} columns={visibleColumns} data={tableData} />
        </CardContent>
      </Card>

      <Dialog
        open={isCompanyModalOpen}
        disableRestoreFocus
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          setCompanyModalOpen(false);
        }}
      >
        <DialogContent>
          <CompanyDetailsForm
            companyData={companyData}
            isLoading={isLoading || isRefetching}
            onConfirm={onUpdateCompany}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={isProductModalOpen}
        maxWidth='lg'
        disableRestoreFocus
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          setProductModalOpen(false);
        }}
      >
        <DialogContent>
          <ProductDetailsForm
            companies={[companyData]}
            productData={selectedProduct}
            isLoading={isRefetching}
            onConfirm={onConfirmProduct}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!selectedProductId}
        onClose={() => setSelectedProductId(undefined)}
        title='Delete Product'
        content='Are you sure you want to delete this product?'
        confirmButton='Delete'
        confirmButtonProps={{ color: 'error', variant: 'contained' }}
        onConfirm={handleDeleteProduct}
      />
    </Container>
  );
};

export default CompanyProfile;
